import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { NgbActiveModal, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { AdminEventService } from '../admin-event/services/admin-event.service';
import { faTimes, faCalendar, faClock } from '@fortawesome/free-solid-svg-icons';
import { AdminEventModel } from '../shared/models/admin-event-models';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';

@Component({
    selector: 'app-admin-event-detail',
    templateUrl: './admin-event-detail.component.html',
    styleUrls: ['./admin-event-detail.component.scss'],
    standalone: false
})
export class AdminEventDetailComponent implements OnInit {
  @Output() eventDeletedOrEdited: EventEmitter<void> = new EventEmitter<void>();
  faTimes = faTimes;
  faCalendar = faCalendar;
  faClock = faClock;
  meridian = true;

  isSaving = false;

  form = this.formBuilder.group({
    date: [undefined],
    startTime: [{ hour: 0, minute: 0 }],
    endTime: [{ hour: 0, minute: 0 }],
    eventTitle: [''],
    location: ['']
  });

  eventDetail: AdminEventModel;
  isNew: boolean;
  startTimeDisplay: string = '';
  endTimeDisplay: string = '';

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private service: AdminEventService,
    private ngbDateParser: NgbDateParserFormatter,
    private toastr: ToastrService) { }

  datePickerMin = {
    year: new Date().getFullYear() - 10,
    month: 1,
    day: 1
  };

  ngOnInit(): void {
    if (this.eventDetail) {
      this.isNew = false;
      const startTimeParts = this.eventDetail.date.split('T')[1].split(':');
      const startTimeObject = {
        hour: parseInt(startTimeParts[0], 10),
        minute: parseInt(startTimeParts[1], 10)
      };

      let endTimeParts;
      let endTimeObject;
      if (this.eventDetail.endDate) {
        endTimeParts = this.eventDetail.endDate.split('T')[1].split(':');
        endTimeObject = {
          hour: parseInt(endTimeParts[0], 10),
          minute: parseInt(endTimeParts[1], 10)
        };
      } else {
        endTimeParts = ['13', '00']; //default when no end time
        endTimeObject = {
          hour: 13,
          minute: 0
        };
      }

      this.form.patchValue({
        date: this.ngbDateParser.parse(this.eventDetail.date ?? ''),
        startTime: startTimeObject,
        endTime: endTimeObject,
        eventTitle: this.eventDetail.eventTitle,
        location: this.eventDetail.location
      });
    } else {
      this.isNew = true;
    }
    this.convertTimeToDisplay();
  }

  onDelete() {
    if (!this.isNew) {
      this.isSaving = true;
      this.service.deleteEvent(this.eventDetail.id)
        .pipe(finalize(() => this.isSaving = false))
        .subscribe((res) => {
          this.modal.dismiss();
          this.toastr.success('Event Deleted Successfully');
          this.eventDeletedOrEdited.emit();
        });
    }
  }

  onEdit() {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;

    const formData = this.form.value;
    const formattedDate = `${formData.date.year}-${formData.date.month.toString().padStart(2, '0')}-${formData.date.day.toString().padStart(2, '0')}T${formData.startTime.hour.toString().padStart(2, '0')}:${formData.startTime.minute.toString().padStart(2, '0')}:00`;
    const formattedEndDate = `${formData.date.year}-${formData.date.month.toString().padStart(2, '0')}-${formData.date.day.toString().padStart(2, '0')}T${formData.endTime.hour.toString().padStart(2, '0')}:${formData.endTime.minute.toString().padStart(2, '0')}:00`;

    const dataModel = {
      id: this.isNew ? 0 : this.eventDetail.id,
      date: formattedDate,
      endDate: formattedEndDate,
      eventTitle: formData.eventTitle,
      location: formData.location
    } as AdminEventModel;

    const request$ = this.isNew ? this.service.createEvent(dataModel) : this.service.updateEvent(dataModel);

    request$
      .pipe(finalize(() => this.isSaving = false))
      .subscribe({
        next: () => {
          this.modal.dismiss();
          this.toastr.success(this.isNew ? 'New Event Created Successfuly' : 'Event Updated Successfully');
          this.eventDeletedOrEdited.emit();
        },
        error: () => {
          this.toastr.error('There was a problem saving the event');
        }
      });
  }

  convertTimeToDisplay() {
    const start = this.form.value.startTime;
    const end = this.form.value.endTime;

    this.startTimeDisplay = this.formatTo12Hour(start.hour, start.minute);
    this.endTimeDisplay = this.formatTo12Hour(end.hour, end.minute);
  }

  formatTo12Hour(hour: number, minute: number): string {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;  // convert 0 -> 12, 13 -> 1, etc.
    const minStr = minute.toString().padStart(2, '0');
    return `${hour12}:${minStr} ${suffix}`;
  }
}
